import { FC, useEffect, useState } from 'react';
import { CreateIcon, EditIcon } from 'assets/icons';
import Guard from 'features/Guard';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { activateDataByCatalogName, deactivateDataByCatalogName, getLookupByName, } from 'store';
import { createOrEditBtn } from 'styles/MUIStyles';

import Button from '@mui/material/Button';

import ConfirmationModal from '../../../features/ConfirmationModal';
import ListItemActions from '../../../features/ListItemActions';
import { Skill } from '../../../models/catalogs';

import { SkillForm } from './SkillForm';

import styles from '../CatalogsLayout.module.scss';

export interface IFormInput {
    name: string;
    skillSetId: number | string;
    description: string;
}

const Skills: FC<{
    name: string;
    createOrUpdate: null | string;
    setCreateOrUpdate: Function;
    fulfilledStatusHandler: Function;
}> = ({
    name,
    createOrUpdate,
    setCreateOrUpdate,
    fulfilledStatusHandler,
}): JSX.Element => {
        const dispatch = useAppDispatch();
        const { t } = useTranslation();

        const skills = useAppSelector((state) => state.catalogs.skills);
        const skillSets = useAppSelector((state) => state.lookups.skillSets);
        const allowedPermissions = useAppSelector(
            (state) => state.auth.allowedPermissions,
        );

        const [isDrawerOpen, setIsDrawerOpen] = useState(false);
        const [activeSkill, setActiveSkill] = useState<null | Skill>(null);
        const [confirmationModalOpen, setConfirmationModalOpen] = useState<{
            isOpen: boolean;
            actionName: string;
        }>({ isOpen: false, actionName: '' });

        const activateItem = async () => {
            if (activeSkill?.id) {
                const response = await dispatch(
                    activateDataByCatalogName({
                        catalogName: name,
                        catalogNameId: activeSkill?.id,
                    }),
                );
                if (response.meta.requestStatus === 'fulfilled') {
                    fulfilledStatusHandler('notifications.successfullyActivated');
                    setConfirmationModalOpen({ isOpen: false, actionName: '' });
                }
            }
        };

        const deactivateItem = async () => {
            if (activeSkill?.id) {
                const response = await dispatch(
                    deactivateDataByCatalogName({
                        catalogName: name,
                        catalogNameId: activeSkill?.id,
                    }),
                );

                if (
                    response.meta.requestStatus === 'fulfilled' &&
                    response.payload
                ) {
                    fulfilledStatusHandler(
                        t('notifications.recordInUseNotice'),
                        'warn',
                    );
                    setConfirmationModalOpen({ isOpen: false, actionName: '' });
                    return;
                }

                if (response.meta.requestStatus === 'fulfilled') {
                    fulfilledStatusHandler('notifications.successfullyDeactivated');
                    setConfirmationModalOpen({ isOpen: false, actionName: '' });
                }
            }
        };
        const onCreateItem = () => {
            setIsDrawerOpen(true);
            setCreateOrUpdate('create');
            setActiveSkill(null);
        };

        const onEditItem = () => {
            setIsDrawerOpen(true);
            setCreateOrUpdate('update');
        };

        const confirmActionHandler = () => {
            switch (confirmationModalOpen.actionName) {
                case 'Activate':
                    activateItem();
                    break;
                case 'Deactivate':
                    deactivateItem();
                    break;
            }
        };

        useEffect(() => {
            dispatch(getLookupByName('skillset'));
        }, [dispatch, name]);

        useEffect(() => {
            setActiveSkill(null);
        }, [dispatch, name]);

        return (
            <div>
                <div className={styles.crudButtonWrapper}>
                    <Guard
                        allowedPermissions={allowedPermissions}
                        permissionName="Create"
                    >
                        <Button
                            startIcon={<CreateIcon />}
                            variant="text"
                            disabled={false}
                            onClick={() => onCreateItem()}
                            sx={createOrEditBtn}
                        >
                            {t('buttons.Create')}
                        </Button>
                    </Guard>
                    <Guard
                        allowedPermissions={allowedPermissions}
                        permissionName="Edit"
                    >
                        <Button
                            startIcon={<EditIcon />}
                            variant="text"
                            disabled={!activeSkill?.isActive}
                            onClick={() => onEditItem()}
                            sx={createOrEditBtn}
                        >
                            {t('buttons.Edit')}
                        </Button>
                    </Guard>
                </div>
                <div className={styles.tableHeader}>
                    <span className={styles.column}>{t('data.name')}</span>
                    <span className={styles.column}>{t('data.description')}</span>
                    <span className={styles.column}>{t('data.skillSet')}</span>
                    <span className={styles.column}>{t('data.actions')}</span>
                </div>
                <ul className={styles.catalogNameDataList}>
                    {skills.map((item) => (
                        <li
                            key={item.id}
                            className={
                                activeSkill?.id === item.id
                                    ? `${styles.catalogNameDataListItem} ${styles.catalogNameDataListItemActive}`
                                    : `${styles.catalogNameDataListItem}`
                            }
                            onClick={() =>
                                setActiveSkill({
                                    id: item.id,
                                    name: item.name,
                                    description: item.description,
                                    isActive: item.isActive,
                                    skillSetId: item.skillSetId,
                                })
                            }
                        >
                            <span className={styles.itemDescription}>{item.name}</span>
                            <span className={styles.itemDescription}>{item.description}</span>
                            <span className={styles.itemDescription}>
                                {
                                    skillSets?.find(
                                        (el) => el.id === item.skillSetId,
                                    )?.name
                                }
                            </span>
                            <span>
                                <ListItemActions
                                    itemId={item.id}
                                    isItemActive={item.isActive}
                                    activationAvailable={true}
                                    setConfirmationModalOpen={
                                        setConfirmationModalOpen
                                    }
                                    editItemClick={onEditItem}
                                />
                            </span>
                        </li>
                    ))}
                </ul>
                <SkillForm
                    name={name}
                    activeSkill={activeSkill}
                    setActiveSkill={setActiveSkill}
                    createOrUpdate={createOrUpdate}
                    setCreateOrUpdate={setCreateOrUpdate}
                    isDrawerOpen={isDrawerOpen}
                    setIsDrawerOpen={setIsDrawerOpen}
                    fulfilledStatusHandler={fulfilledStatusHandler}
                />
                <ConfirmationModal
                    isModalOpen={confirmationModalOpen.isOpen}
                    setIsModalOpen={setConfirmationModalOpen}
                    actionName={confirmationModalOpen.actionName}
                    instanceName={name}
                    handleAction={confirmActionHandler}
                />
            </div>
        );
    };

export default Skills;
